// @ts-nocheck
/**
 * Features Section Component for Sigma08 Landing Page
 * IBM Carbon Design System implementation showcasing platform capabilities
 */

import React, { useState } from 'react';
import {
  Grid,
  Column,
  Heading,
  Stack,
  ExpandableTile,
  TileAboveTheFoldContent,
  TileBelowTheFoldContent,
  Tag,
  Link,
  ContentSwitcher,
  Switch,
} from '@carbon/react';
import {
  Analytics,
  UserMultiple,
  BookmarkFilled,
  ChartLineSmooth,
  Security,
  Mobile,
  Time,
  Award,
  ArrowRight,
  CheckmarkFilled,
  Badge,
} from '@carbon/icons-react';
// import styles from './FeaturesSection.module.scss';
import cx from 'classnames';

interface FeaturesectionProps {
  variant?: 'default' | 'educator' | 'parent' | 'administrator';
  onFeatureInteraction: (feature: string) => void;
}

/**
 * Features Section Component
 * Showcases key platform features with variant-specific emphasis
 */
export const FeaturesSection: React.FC<FeaturesectionProps> = ({ variant = 'default', onFeatureInteraction }) => {
  const [selectedView, setSelectedView] = useState(0);

  // View options for ContentSwitcher
  const viewOptions = [
    { index: 0, name: 'All Features', text: 'All Features' },
    { index: 1, name: 'For Educators', text: 'For Educators' },
    { index: 2, name: 'For Parents', text: 'For Parents' },
    { index: 3, name: 'For Administrators', text: 'For Administrators' },
  ];

  // Get features based on visitor variant or selected view
  const getFeatures = () => {
    const baseFeatures = [
      {
        id: 'personalized-learning',
        icon: BookmarkFilled,
        title: 'Personalized Learning Paths',
        description: "AI-powered curriculum adaptation that adjusts to each student's learning pace and style.",
        benefits: ['Adaptive difficulty', 'Learning style matching', 'Progress optimization'],
        detailedInfo:
          'Our advanced AI analyzes student performance patterns, learning preferences, and engagement metrics to create truly personalized learning paths. The system automatically adjusts content difficulty, suggests alternative learning approaches, and optimizes pacing to maximize comprehension and retention.',
        highlight: variant === 'educator' || variant === 'default',
        badge: 'Popular',
        badgeType: 'blue',
      },
      {
        id: 'real-time-analytics',
        icon: Analytics,
        title: 'Real-Time Progress Analytics',
        description: 'Comprehensive insights into learning progress, engagement, and academic performance.',
        benefits: ['Live dashboards', 'Performance trends', 'Predictive insights'],
        detailedInfo:
          'Monitor student progress in real-time with comprehensive analytics dashboards. Track engagement metrics, identify learning gaps early, and receive predictive insights about student outcomes. Generate detailed reports for parents, administrators, and stakeholders.',
        highlight: variant === 'administrator' || variant === 'parent',
        badge: 'Enhanced',
        badgeType: 'green',
      },
      {
        id: 'collaborative-tools',
        icon: UserMultiple,
        title: 'Seamless Collaboration',
        description: 'Connect students, teachers, and parents with integrated communication and sharing tools.',
        benefits: ['Real-time messaging', 'File sharing', 'Video conferencing'],
        detailedInfo:
          'Foster collaboration with integrated communication tools including real-time messaging, secure file sharing, and built-in video conferencing. Enable seamless parent-teacher conferences, student group work, and administrative communications.',
        highlight: variant === 'parent' || variant === 'default',
      },
      {
        id: 'automated-grading',
        icon: Time,
        title: 'Automated Assessment',
        description: 'Save 75% of grading time with AI-powered assessment and instant feedback systems.',
        benefits: ['Instant feedback', 'Rubric-based grading', 'Plagiarism detection'],
        detailedInfo:
          'Streamline assessment with intelligent auto-grading that handles multiple choice, short answer, and even essay questions. Provide instant feedback to students, detect plagiarism automatically, and maintain consistent grading standards.',
        highlight: variant === 'educator',
        badge: 'New',
        badgeType: 'purple',
      },
      {
        id: 'compliance-security',
        icon: Security,
        title: 'Educational Compliance',
        description: 'FERPA and COPPA compliant platform with enterprise-grade security and privacy controls.',
        benefits: ['FERPA compliant', 'COPPA certified', 'SOC 2 Type II'],
        detailedInfo:
          'Meet all educational compliance requirements with built-in FERPA and COPPA compliance, SOC 2 Type II certification, and enterprise-grade security. Includes data encryption, role-based access controls, and comprehensive audit trails.',
        highlight: variant === 'administrator',
      },
      {
        id: 'mobile-access',
        icon: Mobile,
        title: 'Mobile-First Design',
        description: 'Access learning tools anywhere with responsive design and native mobile apps.',
        benefits: ['iOS/Android apps', 'Offline sync', 'Push notifications'],
        detailedInfo:
          'Learn anywhere, anytime with native mobile apps for iOS and Android. Features offline synchronization, push notifications for important updates, and full feature parity with the web platform for seamless learning on the go.',
        highlight: variant === 'parent' || variant === 'default',
      },
    ];

    // Sort features to show highlighted ones first
    return baseFeatures.sort((a, b) => {
      if (a.highlight && !b.highlight) return -1;
      if (!a.highlight && b.highlight) return 1;
      return 0;
    });
  };

  const features = getFeatures();

  return (
    <section style={{ padding: '4rem 0', backgroundColor: '#f4f4f4' }}>
      <Grid>
        {/* Section Header */}
        <Column lg={16} md={8} sm={4}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Heading style={{ fontSize: '3rem', fontWeight: 300, color: '#161616', marginBottom: '1rem' }}>
              Everything You Need for Educational Excellence
            </Heading>
            <p style={{ fontSize: '1.25rem', color: '#525252', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
              Powerful tools designed specifically for modern education, backed by AI and built for the classroom of
              tomorrow.
            </p>
          </div>
        </Column>

        {/* Features Grid - Using ExpandableTile Components */}
        <div style={{ padding: '2rem 0' }}>
          <Grid>
            {features.map((feature, index) => (
              <Column key={feature.id} lg={8} md={4} sm={4} style={{ marginBottom: '2rem' }}>
                <ExpandableTile
                  style={{
                    height: '100%',
                    border: feature.highlight ? '2px solid #0f62fe' : '1px solid #e0e0e0',
                    backgroundColor: feature.highlight ? '#f6f2ff' : '#ffffff',
                  }}
                  onClick={() => onFeatureInteraction(feature.id)}
                >
                  <TileAboveTheFoldContent>
                    {/* Highlight indicator */}
                    {feature.highlight && (
                      <Tag type="blue" size="sm" style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                        {variant === 'educator' && 'Teacher Favorite'}
                        {variant === 'parent' && 'Parent Essential'}
                        {variant === 'administrator' && 'Admin Priority'}
                        {variant === 'default' && 'Popular'}
                      </Tag>
                    )}

                    <Stack gap={4}>
                      {/* Feature Icon */}
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '1rem',
                          backgroundColor: feature.highlight ? '#e6f0ff' : '#f4f4f4',
                        }}
                      >
                        <feature.icon size={24} style={{ color: feature.highlight ? '#0f62fe' : '#525252' }} />
                      </div>

                      {/* Feature Title */}
                      <Heading
                        style={{ fontSize: '1.25rem', fontWeight: 600, color: '#161616', marginBottom: '0.75rem' }}
                      >
                        {feature.title}
                      </Heading>

                      {/* Feature Description */}
                      <p style={{ color: '#525252', lineHeight: 1.5, fontSize: '0.875rem' }}>{feature.description}</p>
                    </Stack>
                  </TileAboveTheFoldContent>

                  <TileBelowTheFoldContent>
                    <Stack gap={5}>
                      {/* Detailed Information */}
                      <p style={{ color: '#525252', lineHeight: 1.6, fontSize: '0.875rem', marginBottom: '1rem' }}>
                        {feature.detailedInfo}
                      </p>

                      {/* Feature Benefits */}
                      <div>
                        <Heading
                          style={{ fontSize: '0.75rem', color: '#161616', marginBottom: '1rem', fontWeight: 600 }}
                        >
                          Key Benefits:
                        </Heading>
                        <Stack gap={3}>
                          {feature.benefits.map((benefit, benefitIndex) => (
                            <div
                              key={benefitIndex}
                              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}
                            >
                              <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.875rem', color: '#161616' }}>{benefit}</span>
                            </div>
                          ))}
                        </Stack>
                      </div>

                      {/* Action Link */}
                      <Link
                        href="#"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          fontSize: '0.875rem',
                          marginTop: '1rem',
                        }}
                        onClick={e => {
                          e.preventDefault();
                          onFeatureInteraction(feature.id);
                        }}
                      >
                        Learn more about {feature.title.toLowerCase()}
                        <ArrowRight size={16} />
                      </Link>
                    </Stack>
                  </TileBelowTheFoldContent>
                </ExpandableTile>
              </Column>
            ))}
          </Grid>
        </div>

        {/* Bottom CTA */}
        <Column lg={16} md={8} sm={4}>
          <div
            style={{
              textAlign: 'center',
              marginTop: '4rem',
              padding: '2rem',
              backgroundColor: '#f4f4f4',
              borderRadius: '8px',
            }}
          >
            <Heading style={{ fontSize: '2rem', fontWeight: 400, color: '#161616', marginBottom: '1rem' }}>
              Ready to Experience These Features?
            </Heading>
            <p style={{ fontSize: '1rem', color: '#525252', marginBottom: '2rem' }}>
              Join thousands of educators already transforming their classrooms with Sigma08.
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 600, marginBottom: '0.5rem', color: '#0f62fe' }}>
                  10,000+
                </div>
                <div style={{ fontSize: '0.75rem', color: '#525252' }}>Active Educators</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 600, marginBottom: '0.5rem', color: '#24a148' }}>
                  50M+
                </div>
                <div style={{ fontSize: '0.75rem', color: '#525252' }}>Student Interactions</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 600, marginBottom: '0.5rem', color: '#0f62fe' }}>95%</div>
                <div style={{ fontSize: '0.75rem', color: '#525252' }}>Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </Column>
      </Grid>
    </section>
  );
};

export default FeaturesSection;
